// src/features/notes/notesApiSlice.js
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

// --- Normalized adapter ---
const notesAdapter = createEntityAdapter({
  // use our own string "id" we set in transformResponse
  selectId: (c) => c.id,
});

// Keep a stable extra field for pagination metadata
const initialState = notesAdapter.getInitialState({ total: 0 });

export const notesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNotes: builder.query({
        // args: { page, limit, q, status, sortBy, order, ... }
        query: (params = {}) => ({ url: '/notes', params }),

        transformResponse: (response, meta) => {
          let items = [];
          let total = 0;

          if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
            items = response.items ?? [];
            total = Number(response.total ?? 0);
          } else if (Array.isArray(response)) {
            items = response;
            const headerTotal =
              meta?.response?.headers?.get('X-Total-Count') ??
              meta?.response?.headers?.get('x-total-count');
            total = headerTotal ? Number(headerTotal) : items.length;
          }

          // 1) Force a *single* stable string key, and set BOTH id and _id to it
          const processed = items.map((c) => {
            const raw = c?._id ?? c?.id;
            const sid = raw != null ? String(raw) : undefined;
            return { ...c, id: sid, _id: sid };
          });

          // 2) Manual normalization using *id* as the key (matches adapter.selectId)
          const ids = [];
          const entities = {};
          for (const c of processed) {
            if (c.id) {
              ids.push(c.id);
              entities[c.id] = c;
            }
          }

          // Optional debug (remove after verifying)
          // console.log('normalized (id):', { idsLen: ids.length, total, first: ids[0] });

          return { ids, entities, total };
        },
      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Note', id })),
              { type: 'Note', id: 'LIST' }, // ⬅️ use LIST consistently
            ]
          : [{ type: 'Note', id: 'LIST' }],
    }),

    getNoteById: builder.query({
      query: (id) => `/notes/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Note', id }],
    }),

    addNewNote: builder.mutation({
      query: (body) => ({ url: '/notes', method: 'POST', body }),
      invalidatesTags: [{ type: 'Note', id: 'LIST' }], // ⬅️ matches providesTags
    }),

    updateNote: builder.mutation({
      query: ({ id, ...patch }) => ({ url: `/notes/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'Note', id }, { type: 'Note', id: 'LIST' }],
    }),

    deleteNote: builder.mutation({
      query: ({ id }) => ({ url: `/notes/${id}`, method: 'DELETE', body: { id } }),
      invalidatesTags: [{ type: 'Note', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetNotesQuery,
  useGetNoteByIdQuery,
  useAddNewNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = notesApiSlice;

/* -----------------------
   Arg-aware selectors
   ----------------------- */

// Base result selector factory (must pass the same args you use in the hook)
const selectGetNotesResult = notesApiSlice.endpoints.getNotes.select;

export const makeSelectNotesData = (args) =>
  createSelector(selectGetNotesResult(args), (res) => res?.data ?? initialState);

// Export a factory that returns normalized selectors + total for a given args set
export const makeNoteSelectors = (args) => {
  const selectData = makeSelectNotesData(args);
  const base = notesAdapter.getSelectors((state) => selectData(state));
  return {
    selectAllNotes: base.selectAll,
    selectNoteById: base.selectById,
    selectNoteIds: base.selectIds,
    selectNoteEntities: base.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  };
};
